import { z } from "zod";
import { metricValueSchema } from "./metrics.js";
import { siteKindSchema } from "./off-page-check.js";
/**
 * The Overview boundary.
 *
 * One report, one date range, and every number on the screen. It is a single
 * payload rather than six endpoints because every widget answers a question
 * about the same set of captures — six requests could return six views of a
 * moving set, and the screen would show a share of voice that does not add up
 * against a total from a second earlier.
 */
const isoDate = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/u, "expected an ISO date, e.g. 2026-08-01");
/**
 * The parts of the domain-sources ring, in the order it draws them.
 *
 * The first two are facts we hold: a site is yours, or a tracked rival's,
 * because `brand_domains` says so. The next three are kinds of site, and a
 * kinds are a categorisation (Off-page's, by the site's own pages or a model)
 * — so the screen says where each came from. `companies` holds other firms'
 * sites and shops, and every tracked brand that is not this report's. `unsorted` is every site we have not
 * categorised, shown as its own part rather than folded into "other": a share
 * we do not know is not a share of anything.
 */
export const DOMAIN_SOURCE_GROUPS = [
    "yours",
    "competitors",
    "publications",
    "community",
    "companies",
    "other",
    "unsorted",
];
export const domainSourceGroupSchema = z.enum(DOMAIN_SOURCE_GROUPS);
/**
 * Why a cited site sits in its part. A tracked brand that is neither this
 * report's subject nor one of its rivals belongs to another report in the
 * workspace; it is named as such, in `companies`, rather than given a kind we
 * never worked out.
 */
export const domainSourceSchema = z.discriminatedUnion("kind", [
    z.object({ kind: z.literal("yours") }),
    z.object({ kind: z.literal("competitor"), brandName: z.string() }),
    z.object({ kind: z.literal("tracked"), brandName: z.string() }),
    z.object({ kind: z.literal("site"), site: siteKindSchema }),
]);
/**
 * One cited site, with its part of the ring and why it sits there. The
 * Overview's top ten and a part's full list (`domain-source-sites.ts`) share
 * it, so a site reads the same in both.
 */
export const topDomainSchema = z.object({
    domain: z.string(),
    citations: z.number().int().min(0),
    group: domainSourceGroupSchema,
    source: domainSourceSchema,
});
export const overviewFilterSchema = z.object({
    from: isoDate,
    to: isoDate,
    engine: z.string().trim().min(1).max(40).optional(),
});
/** One capture day. Days with no captures are absent, never zero-filled. */
export const overviewDaySchema = z.object({
    day: isoDate,
    responses: z.number().int().min(0),
    mentioning: z.number().int().min(0),
    /**
     * Mean position among the brands each answer named, or null when unnamed.
     *
     * Null, never 0 — position 1 is the best there is, so a zero would plot
     * better than first place on an axis where lower is better.
     */
    averageRank: z.number().nullable(),
    /**
     * 0–100, the model's reading of how strongly that day's answers steered a
     * reader towards the brand. Null when none were read.
     *
     * **Modelled, never observed.** Renaming a column does not turn a judgement
     * into an observation, and every surface that plots this says so.
     */
    likelihoodToBuy: z.number().nullable(),
});
export const standingSchema = z.object({
    brandId: z.string(),
    brandName: z.string(),
    /** How many answers named it. The numerator of its coverage. */
    responsesNaming: z.number().int().min(0),
    /**
     * How many answers named it **first**.
     *
     * The measured analogue of a prominence score. Being named and being named
     * first are different achievements, and this counts the second rather than
     * inferring it from an average — which is what makes it a number the screen
     * is allowed to show as a percentage.
     */
    namedFirst: z.number().int().min(0),
    occurrences: z.number().int().min(0),
    averageRank: z.number(),
    /**
     * Share of every mention in the range, in basis points.
     *
     * Integer, not a float — AGENTS.md §7 keeps percentages in basis points so
     * two clients rounding differently cannot disagree about a number they were
     * both handed.
     */
    shareOfVoiceBp: z.number().int().min(0).max(10_000),
    /**
     * How answers spoke about it, as a net score from -100 to 100, or null when
     * no answer has been read for tone.
     *
     * Null rather than 0: a balanced brand and an unscored one are opposite
     * states, and a zero would make them look identical. `sentimentScored`
     * carries the sample so the screen can say which it is.
     */
    sentimentNet: z.number().int().min(-100).max(100).nullable(),
    sentimentScored: z.number().int().min(0),
    /**
     * What the net was computed from: of `sentimentScored`, how many answers
     * read positive and how many negative; the rest were neutral. A net of +86
     * says nothing about whether 3 answers spoke badly of the brand.
     */
    sentimentPositive: z.number().int().min(0),
    sentimentNegative: z.number().int().min(0),
    /**
     * 0–100: on average, how strongly answers steered a reader towards this
     * brand. Null when no answer has been read for it.
     *
     * **Modelled, not observed.** It is a language model's reading of prose we
     * hold, at the same tier as sentiment, and it is never averaged with a
     * counted number. `likelihoodToBuyScored` carries the sample so the screen can
     * say how much is behind it.
     */
    likelihoodToBuyAvg: z.number().int().min(0).max(100).nullable(),
    likelihoodToBuyScored: z.number().int().min(0),
    /** The brand this report is about. Exactly one row carries it. */
    isSubject: z.boolean(),
    /**
     * False for a brand the answers named that this report never listed.
     *
     * Shown differently, not hidden: a competitor beating you on your own
     * questions matters whether or not somebody remembered to add it.
     */
    isListed: z.boolean(),
});
/**
 * The days just before the range, of the same length, and how the subject
 * stood in them — the Overview's "versus the days before".
 *
 * Counts, not changes: the screen says the change in words, and the counts let
 * it say what the change is of. `comparable` is the thin-data rule the emails
 * use (`MIN_ANSWERS`, 30 answers on each side): below it a movement is noise,
 * and the screen says so rather than drawing an arrow.
 */
export const previousPeriodSchema = z.object({
    from: isoDate,
    to: isoDate,
    /** Every answer in those days on this report's questions, every engine. */
    responses: z.number().int().min(0),
    /** Answers each side needs before the two are compared. */
    required: z.number().int().min(1),
    comparable: z.boolean(),
    /** The subject in those days, or null when no answer named it. */
    subject: z
        .object({
        responsesNaming: z.number().int().min(1),
        averageRank: z.number().min(1),
    })
        .nullable(),
});
export const overviewSchema = z.object({
    reportId: z.string(),
    reportName: z.string(),
    brandId: z.string(),
    brandName: z.string(),
    from: isoDate,
    to: isoDate,
    /** Every answer in range on this report's questions. The denominator. */
    responses: z.number().int().min(0),
    previous: previousPeriodSchema,
    /**
     * Share of answers naming the subject brand.
     *
     * A `MetricValue`, so the screen cannot compile without handling "not enough
     * yet" — and so it renders "needs 1, you have 0" rather than a confident 0%.
     */
    coverage: metricValueSchema,
    timeline: z.array(overviewDaySchema),
    /**
     * The same days, for the subject and every competitor.
     *
     * Separate from `timeline` rather than replacing it: the subject's own line
     * is what the coverage figure above the chart is computed from, and a screen
     * that had to find its own brand inside a list before it could show one
     * number is a screen with a bug waiting in it.
     *
     * Every series shares one denominator per day, because they come from one
     * query. Two queries landing either side of a capture finishing would give
     * lines measured against different totals, which is not a comparison.
     */
    timelines: z.array(z.object({
        brandId: z.string(),
        brandName: z.string(),
        isSubject: z.boolean(),
        days: z.array(overviewDaySchema),
    })),
    standings: z.array(standingSchema),
    /**
     * The questions that name the subject most, with their denominators.
     *
     * `mentioning` alone is unreadable — 16 out of 16 and 16 out of 60 are
     * opposite findings — so the answer count travels with it and the screen
     * shows both.
     */
    /**
     * The pages these answers cited most, and how many citations there were in
     * total — the denominator, so a share is against every citation in range
     * rather than against the ten rows the screen shows.
     */
    citations: z.object({
        total: z.number().int().min(0),
        urls: z.array(z.object({
            url: z.string(),
            /** Null when the URL will not reduce. Still counted, just no favicon. */
            domain: z.string().nullable(),
            citations: z.number().int().min(0),
        })),
    }),
    /**
     * How often each brand's own sites were cited — a different finding from how
     * often the brand was *named*. An answer can praise you all month while every
     * link in it goes somewhere else.
     */
    domains: z.object({
        /** Every citation in range, the denominator both shares are taken against. */
        total: z.number().int().min(0),
        brands: z.array(z.object({
            brandId: z.string(),
            brandName: z.string(),
            isSubject: z.boolean(),
            citations: z.number().int().min(0),
            days: z.array(z.object({
                day: isoDate,
                citations: z.number().int().min(0),
                total: z.number().int().min(0),
            })),
        })),
        /**
         * The sites cited most, by domain rather than by page.
         *
         * A publisher with twenty different articles is invisible in the page list
         * and obvious here. Each row carries its part of the ring and why, so the
         * table and the ring cannot disagree about a site. Shares are taken against
         * `sources.total`, the same whole the ring draws.
         */
        topDomains: z.array(topDomainSchema),
        /**
         * Every citation with a site, split by what kind of site it went to. Its
         * `total` is the ring's whole and the denominator of every share in the
         * card, so the parts add up to it. Citations whose URL would not reduce to
         * a site are the difference between this and `domains.total`, and the
         * screen says how many there were rather than leaving them out silently.
         */
        sources: z.object({
            total: z.number().int().min(0),
            groups: z.array(z.object({
                group: domainSourceGroupSchema,
                citations: z.number().int().min(0),
                /** How many different sites make up this part. */
                domains: z.number().int().min(0),
            })),
        }),
        /**
         * Questions that got the subject's **own** sites cited. The most directly
         * actionable list here: an engine already trusts these pages enough to link
         * them, which is a different list from the questions that merely name you.
         */
        promptsByCitation: z.array(z.object({
            promptId: z.string(),
            text: z.string(),
            citations: z.number().int().min(0),
        })),
        /** The subject's own most-cited pages. Its own sites, not everyone's. */
        ownUrls: z.array(z.object({
            url: z.string(),
            domain: z.string().nullable(),
            citations: z.number().int().min(0),
        })),
    }),
    topPrompts: z.array(z.object({
        promptId: z.string(),
        text: z.string(),
        responses: z.number().int().min(0),
        mentioning: z.number().int().min(0),
    })),
});
//# sourceMappingURL=overview.js.map